// ------------------------------------------------------------------------------
// name: ChunkUploader
// author: mudas
// created: 2024-10-15
// ------------------------------------------------------------------------------

import { range } from 'lodash-es';
import { isCanceledError } from 'common/utils';

import { RcFile } from 'antd/es/upload/interface';
import { ChunkOption, ChunkStatus, ChunkUploadConfig, ChunkUploadResult, ChunkUploadId } from './types';
import { FileACL } from 'common/types';
import { BaseUploader } from './BaseUploader';
import UploadError from './UploadError';

import service from 'common/service';

export class ChunkUploader extends BaseUploader {
  protected file?: RcFile;
  protected options?: ChunkOption;

  // 初始化分片上传的索引ID（获取预签名 url 的前置条件）
  protected chunkId: ChunkUploadId | null = null;

  // 用于取消请求
  protected controller: AbortController | null = new AbortController();

  // 实际数值型分片大小
  protected realChunkSize: number = ChunkUploadConfig.minChunkSize;

  // 总分片数量
  protected totalChunk: number = 0;

  // 分片上传完成返回结果
  protected partEtags: ChunkUploadResult[] = [];

  // 待上传的切片
  protected pending: number[] = [];
  protected percents: number[] = []; // 上传进度

  // 正在上传的切片（上传完成后，才会从 pending 中移除，向后设计考虑未来断点续传）
  protected running: number[] = [];

  // --------------------------------------------------------------------------
  //
  // Class constructor
  //
  // --------------------------------------------------------------------------

  constructor(
    // 1~20 之间的整数，代表同时开始上传的任务线程数，原则上越多越快，但会给服务器带来压力。
    protected thread: number = ChunkUploadConfig.thread,
    // 分处上传的大小（默认：auto）
    // auto - 智能模式，取文件大小的 10% 做为分片大小，且最小不小于 ChunkUploadConfig.minChunkSize，
    // 最大不超过 ChunkUploadConfig.maxChunkSize
    protected chunkSize: number | 'auto' = 'auto',
  ) {
    super();

    this.checkThread();
    this.checkChunkSize();
  }

  // --------------------------------------------------------------------------
  //
  // Class methods
  //
  // --------------------------------------------------------------------------

  checkThread(thread?: number) {
    if (thread && (thread < 1 || thread > 20)) throw new Error('The parameter `thread` is illegal');
  }

  checkChunkSize(chunkSize?: number | 'auto') {
    const numberChunkSize =
      typeof chunkSize === 'number' &&
      chunkSize >= ChunkUploadConfig.minChunkSize &&
      chunkSize <= ChunkUploadConfig.maxChunkSize;

    const stringChunkSize = typeof chunkSize === 'string' && chunkSize === 'auto';

    if (chunkSize && (!numberChunkSize || !stringChunkSize)) {
      throw new Error('The parameter `chunkSize` is illegal');
    }
  }

  init(file: RcFile) {
    const autoSize = Math.max(
      ChunkUploadConfig.minChunkSize,
      Math.min(ChunkUploadConfig.maxChunkSize, file.size * 0.1),
    );

    this.realChunkSize = Math.ceil(this.chunkSize === 'auto' ? autoSize : this.chunkSize);

    // 空文件，需要支持上传的话，需要至少 totalChunk = 1
    this.totalChunk = Math.ceil(file.size / this.realChunkSize);
    this.pending = range(this.totalChunk);
    this.percents = range(this.totalChunk);
  }

  // 更新进度
  updatePercent() {
    const percent = this.percents.reduce((acc, cur) => acc + (cur ?? 0), 0) / this.totalChunk;
    this.options?.onProgress?.(percent, this.file!);
    return percent;
  }

  /**
   * 获取指定切片的文件数据
   * @param file
   * @param index
   */
  getChunkData(file: RcFile, index: number): Blob {
    if (index < 0 || index > this.totalChunk) throw new Error(`ChunkUploader: Invalid slice number: ${index}`);

    let startPos = index * this.realChunkSize;
    let endPos = (index + 1) * this.realChunkSize;

    // 检测是否到最后分块（调整到实际文件大小的终止点）
    if (endPos > file.size) endPos = file.size;

    // 异常起点、终点
    // 若要允许上传空文件，需要此处放开限制 endPos
    if (startPos < 0 || startPos >= file.size || endPos <= 0 || endPos <= startPos) {
      throw new Error(`ChunkUploader: the beginning or end of the anomaly, start[${startPos}] end[${endPos}]`);
    }

    return file.slice(startPos, endPos);
  }

  /**
   * 获取指定个数未开始上传的切片序号
   * @param thread
   */
  getNextChunks(thread: number) {
    // 剩余未开始的分片
    const notRunningChunks = this.pending.filter(index => !this.running.includes(index));

    if (notRunningChunks.length > 0) {
      // 剩余可用的空闲线程数量
      const surplusThread = Math.max(0, thread - this.running.length); // 当前可允许开启的线程数

      return surplusThread > 0 ? notRunningChunks.slice(0, surplusThread) : [];
    }

    return [];
  }

  // 具体分片内容的上传（上传内容）
  async uploadChunkContent(chunkIndex: number, retryCount: number = 3): Promise<ChunkStatus> {
    if (!this.chunkId) return Promise.resolve(ChunkStatus.NotReady);

    const { fileId, chunkUploadId } = this.chunkId;

    const sign = await service.chunkUploads.getUploadChunkPreSignUrl(
      {
        fileId,
        chunkUploadId,
        index: chunkIndex + 1, // 前端从0开始，后端服务从1开始
      },
      {
        signal: this.controller?.signal,
      },
    );

    // 是否已经暂停上传
    if (this.paused) {
      return Promise.resolve(ChunkStatus.Paused);
    }

    // 初始化获取预签名上传地址失败
    else if (!sign || !sign.data || !sign.data.preSignedChunkUploadUrl) {
      return Promise.resolve(ChunkStatus.GetUrlFailed);
    }

    const blob = this.getChunkData(this.file!, chunkIndex);
    return service.chunkUploads
      .uploadChunk(
        { url: sign.data.preSignedChunkUploadUrl, blob },
        {
          signal: this.controller?.signal,
          onUploadProgress: event => {
            this.percents[chunkIndex] = (event.progress ?? 0) * 100;
            this.updatePercent();
          },
        },
      )
      .then(({ data }) => {
        // 记录 tag data
        if (!this.partEtags.some(tag => +tag.part_number === chunkIndex + 1)) {
          this.partEtags.push(data.partEtag);
        }

        return Promise.resolve(ChunkStatus.Completed);
      })
      .catch(reason => {
        // 暂停上传
        if (isCanceledError(reason)) return Promise.resolve(ChunkStatus.Paused);
        if (retryCount > 0) {
          console.warn(`Chunk [${chunkIndex}] upload failed. Retrying... (${3 - retryCount + 1}/3)`);

          // 根据重试次数设置阶梯式等待时间
          const retryAttempt = 3 - retryCount + 1; // 计算当前是第几次重试
          const delayTime = retryAttempt === 1 ? 1000 : retryAttempt === 2 ? 2000 : 5000; // 分别等待1秒、2秒、5秒

          return new Promise<ChunkStatus>(resolve => {
            setTimeout(() => {
              resolve(this.uploadChunkContent(chunkIndex, retryCount - 1));
            }, delayTime);
          });
        }
        const error = new Error(reason.message || `分片 [${chunkIndex}] 上传失败`);
        return Promise.reject(error);
      });
  }

  // 所有分片上传完成，调用服务端聚合服务
  concatChunks() {
    if (!this.chunkId) return Promise.resolve(ChunkStatus.NotReady);

    const { fileId, chunkUploadId } = this.chunkId;
    return (
      service.chunkUploads
        .chunksConcat({
          fileId,
          chunkUploadId,
          partEtags: this.partEtags,
        })
        // .then(({ data }) => {})
        .catch(reason => {
          const error = new Error(reason.message || `文件上传意外错误`);
          return Promise.reject(error);
        })
    );
  }

  // 执行指定分片上传任务（调度和结果处理）
  uploadChunk(chunkIndex: number) {
    const { onChunkInit, onChunkFailed, onChunkCompleted, onFinished, onFailed } = this.options || {};

    if (chunkIndex >= 0) {
      // 标记正在执行的切片
      this.running.push(chunkIndex);

      // 向外派发切片初始上传事件
      onChunkInit?.(chunkIndex);

      this.uploadChunkContent(chunkIndex, 3)
        .then(status => {
          switch (status) {
            case ChunkStatus.NotReady:
            case ChunkStatus.GetUrlFailed:
            case ChunkStatus.Error: {
              // 若失败则将该切片还原到未开始状态（pending 中的切片只有上传完成时才会被移除）
              this.running = this.running.filter(i => i !== chunkIndex);

              // 事件派发，外部可以标记失败状态栏，待重试
              onChunkFailed?.(chunkIndex);
              break;
            }

            case ChunkStatus.Paused: {
              // 暂停则将该切片还原到未开始状态（pending 中的切片只有上传完成时才会被移除）
              this.running = this.running.filter(i => i !== chunkIndex);
              break;
            }

            case ChunkStatus.Completed: {
              // 切片上传完成
              onChunkCompleted?.(chunkIndex);

              // 更新任务列表
              this.pending = this.pending.filter(i => i !== chunkIndex);
              this.running = this.running.filter(i => i !== chunkIndex);

              // 切片关系处理完成后，更新进度
              this.updatePercent();

              // 若有未完成的切片，则继续上传
              if (this.pending.length > 0) {
                this.execUploadChunks();
              }
              // 若没有待上传的切片，则代表整个文件上传完成
              else {
                this.concatChunks()
                  .then(() => {
                    onFinished?.({ ...this.chunkId!, url: this.chunkId?.downloadUrl }, this.file!);
                  })
                  .catch(reason => {
                    // 聚合失败（待设计重试功能）
                    onFailed?.(new UploadError(reason.message, this.file, ChunkStatus.ConcatError));
                  });
              }

              break;
            }
          }
        })
        .catch(reason => {
          onChunkFailed?.(chunkIndex);

          // 切片上传失败，整个文件也同样失败
          onFailed?.(new UploadError(reason.message, this.file, ChunkStatus.Error));
        });
    }
  }

  // 根据设定的线程数批量开启一批新的分片上传进程
  execUploadChunks() {
    // 多任务并行发送执行，各自处理自己的结束逻辑
    const chunks = this.getNextChunks(this.thread);

    chunks.forEach(index => {
      this.uploadChunk(index);
    });
  }

  stop() {
    this.running = []; // 去除所有正在进行上传的切片，未完成切片皆包含在 pending 中
    this.controller?.abort();
    this.controller = null;
    this._paused = true;
  }

  resume() {
    // 若非正常途径来恢复，则停止上次的上传进程，重新开始
    // 正常途径会通过 stop() 方法停止上传，并置为 null
    if (this.controller) this.stop();

    // 单个文件从0开始重新上传
    this.controller = new AbortController();
    this._paused = false;
    this.execUploadChunks();
  }

  /**
   * 开始上传文件（分片模式）
   * @param file
   * @param options
   */
  async upload(file: RcFile, options?: ChunkOption) {
    this.file = file;
    this.options = options;

    const { thread, chunkSize } = options || {};

    // 参数非法会中断此文件的上传
    this.checkThread();
    this.checkChunkSize();

    if (thread) this.thread = thread;
    if (chunkSize) this.chunkSize = chunkSize;

    this.init(file);

    // 初始化文件上传id
    const params = {
      fileName: file.name,
      fileSize: file.size.toString(),
      contentType: file.type,
      acl: FileACL.Private,
      ...options?.params, // 从外部根据业务逻辑自定义进行覆盖
    };
    this.chunkId = await service.chunkUploads.getUploadChunkId(params);

    if (!this.chunkId) {
      options?.onFailed?.(new UploadError('分片上传的索引ID获取失败', file, ChunkStatus.Error));
      return false;
    }

    // 需要异步返回，又不能破坏 execChunkUpload 并行执行的特性
    this.execUploadChunks();
  }
}
